import { db, storage } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  writeBatch,
  query,
  orderBy,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export const leitnerService = {
  // Get all questions for a book
  async getBookQuestions(bookId) {
    try {
      const questionsRef = collection(db, 'pb-books', bookId, 'questions');
      const q = query(questionsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const questions = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const questionData = { id: docSnapshot.id, ...docSnapshot.data() };
          
          if (questionData.type === 'stem') {
            const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', docSnapshot.id, 'subquestions');
            const subQSnapshot = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
            questionData.subQuestions = subQSnapshot.docs.map(subDoc => ({
              id: subDoc.id,
              ...subDoc.data()
            }));
          }
          
          return questionData;
        })
      );
      
      return questions;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },

  // Get learner's progress for all questions
  async getLearnerProgress(bookId, userId) {
    try {
      const progressRef = collection(db, 'pb-books', bookId, 'learnerProgress', userId, 'questions');
      const snapshot = await getDocs(progressRef);
      
      const progress = {};
      snapshot.docs.forEach(doc => {
        progress[doc.id] = doc.data();
      });

      // Get subquestion progress
      const subProgressRef = collection(db, 'pb-books', bookId, 'learnerProgress', userId, 'subquestions');
      const subSnapshot = await getDocs(subProgressRef);
      
      const subProgress = {};
      subSnapshot.docs.forEach(doc => {
        subProgress[doc.id] = doc.data();
      });

      return { questions: progress, subquestions: subProgress };
    } catch (error) {
      console.error('Error fetching learner progress:', error);
      throw error;
    }
  },

  // Initialize progress for a question (if not exists)
  async initializeQuestionProgress(bookId, userId, questionId, isSubQuestion = false, parentQuestionId = null) {
    try {
      const progressPath = isSubQuestion 
        ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${questionId}`
        : `pb-books/${bookId}/learnerProgress/${userId}/questions/${questionId}`;
      
      const progressRef = doc(db, progressPath);
      const progressDoc = await getDoc(progressRef);

      if (!progressDoc.exists()) {
        await setDoc(progressRef, {
          box: 1,
          attempts: 0,
          correctAttempts: 0,
          lastReviewed: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(isSubQuestion && { parentQuestionId })
        });
      }
    } catch (error) {
      console.error('Error initializing question progress:', error);
      throw error;
    }
  },

  // Get questions with learner progress merged
  async getQuestionsWithProgress(bookId, userId) {
    try {
      const questions = await this.getBookQuestions(bookId);
      const progress = await this.getLearnerProgress(bookId, userId);

      return questions.map(q => {
        if (q.type === 'simple') {
          const questionProgress = progress.questions[q.id] || { box: 1, attempts: 0, correctAttempts: 0 };
          return { ...q, ...questionProgress };
        } else if (q.type === 'stem') {
          const subQuestionsWithProgress = (q.subQuestions || []).map(subQ => {
            const subProgress = progress.subquestions[subQ.id] || { box: 1, attempts: 0, correctAttempts: 0 };
            return { ...subQ, ...subProgress };
          });
          return { ...q, subQuestions: subQuestionsWithProgress };
        }
        return q;
      });
    } catch (error) {
      console.error('Error getting questions with progress:', error);
      throw error;
    }
  },

  // Add a new question
  async addQuestion(bookId, questionData, imageFile = null) {
    try {
      let stemImage = null;
      
      if (imageFile) {
        const imagePath = `pb-books/${bookId}/questions/${Date.now()}_${imageFile.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, imageFile);
        stemImage = await getDownloadURL(imageRef);
      }

      const { subQuestions, ...mainQuestionData } = questionData;

      const questionToAdd = {
        ...mainQuestionData,
        stemImage,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const questionsRef = collection(db, 'pb-books', bookId, 'questions');
      const docRef = await addDoc(questionsRef, questionToAdd);

      if (questionData.type === 'stem' && subQuestions && subQuestions.length > 0) {
        const batch = writeBatch(db);
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', docRef.id, 'subquestions');
        
        subQuestions.forEach((subQ, index) => {
          const subQRef = doc(subQuestionsRef);
          batch.set(subQRef, {
            ...subQ,
            order: index,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        const createdSubQs = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
        questionToAdd.subQuestions = createdSubQs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.questionCount || 0;
      
      const countIncrement = questionData.type === 'stem' 
        ? (subQuestions?.length || 0) 
        : 1;
      
      await updateDoc(bookRef, {
        questionCount: currentCount + countIncrement,
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...questionToAdd };
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  },

  // Update a question
  async updateQuestion(bookId, questionId, questionData, imageFile = undefined, existingImageUrl = null) {
    try {
      let stemImage = existingImageUrl;

      // Only process if imageFile is explicitly provided
      if (imageFile instanceof File) {
        // Delete old image if exists
        if (existingImageUrl) {
          try {
            const oldImageRef = ref(storage, existingImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log('Old image not found or already deleted');
          }
        }

        // Upload new image
        const imagePath = `pb-books/${bookId}/questions/${Date.now()}_${imageFile.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, imageFile);
        stemImage = await getDownloadURL(imageRef);
      } else if (imageFile === null) {
        // Explicit deletion request - remove image
        if (existingImageUrl) {
          try {
            const oldImageRef = ref(storage, existingImageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log('Image not found or already deleted');
          }
        }
        stemImage = null;
      }
      // If imageFile is undefined, keep existing image (no change)

      const { subQuestions, ...mainQuestionData } = questionData;

      const questionRef = doc(db, 'pb-books', bookId, 'questions', questionId);
      const updateData = {
        ...mainQuestionData,
        stemImage,
        updatedAt: serverTimestamp()
      };

      await updateDoc(questionRef, updateData);

      if (questionData.type === 'stem' && subQuestions) {
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', questionId, 'subquestions');
        
        const existingSubQs = await getDocs(subQuestionsRef);
        const batch = writeBatch(db);
        existingSubQs.docs.forEach(doc => batch.delete(doc.ref));
        
        subQuestions.forEach((subQ, index) => {
          const subQRef = doc(subQuestionsRef);
          batch.set(subQRef, {
            ...subQ,
            order: index,
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        const updatedSubQs = await getDocs(query(subQuestionsRef, orderBy('order', 'asc')));
        updateData.subQuestions = updatedSubQs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      return { id: questionId, ...updateData };
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  },

  // Delete a question
  async deleteQuestion(bookId, questionId, imageUrl = null) {
    try {
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.log('Image not found or already deleted');
        }
      }

      const questionRef = doc(db, 'pb-books', bookId, 'questions', questionId);
      const questionDoc = await getDoc(questionRef);
      const questionData = questionDoc.data();

      let deletedCount = 1;
      if (questionData?.type === 'stem') {
        const subQuestionsRef = collection(db, 'pb-books', bookId, 'questions', questionId, 'subquestions');
        const subQSnapshot = await getDocs(subQuestionsRef);
        deletedCount = subQSnapshot.size;
        
        const batch = writeBatch(db);
        subQSnapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
      }

      await deleteDoc(questionRef);

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.questionCount || 0;
      
      await updateDoc(bookRef, {
        questionCount: Math.max(0, currentCount - deletedCount),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  },

  // Update learner's progress for a question
  async updateLearnerProgress(bookId, userId, questionId, isCorrect, isSubQuestion = false, parentQuestionId = null) {
    try {
      const progressPath = isSubQuestion 
        ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${questionId}`
        : `pb-books/${bookId}/learnerProgress/${userId}/questions/${questionId}`;
      
      const progressRef = doc(db, progressPath);
      const progressDoc = await getDoc(progressRef);

      let currentBox = 1;
      let attempts = 0;
      let correctAttempts = 0;

      if (progressDoc.exists()) {
        const data = progressDoc.data();
        currentBox = data.box || 1;
        attempts = data.attempts || 0;
        correctAttempts = data.correctAttempts || 0;
      }

      // Determine next box
      let nextBox = currentBox;
      if (isCorrect) {
        switch (currentBox) {
          case 1: nextBox = 2; break;
          case 2: nextBox = 3; break;
          case 3: nextBox = 4; break;
          default: nextBox = 4;
        }
      } else {
        nextBox = 1; // Move back to box 1 on incorrect answer
      }

      const updateData = {
        box: nextBox,
        attempts: attempts + 1,
        correctAttempts: isCorrect ? correctAttempts + 1 : correctAttempts,
        lastReviewed: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(isSubQuestion && !progressDoc.exists() && { parentQuestionId })
      };

      await setDoc(progressRef, updateData, { merge: true });

      return { box: nextBox };
    } catch (error) {
      console.error('Error updating learner progress:', error);
      throw error;
    }
  },

  // Batch update learner progress (for test completion)
  async batchUpdateLearnerProgress(bookId, userId, updates) {
    try {
      const batch = writeBatch(db);

      for (const update of updates) {
        const progressPath = update.isSubQuestion
          ? `pb-books/${bookId}/learnerProgress/${userId}/subquestions/${update.questionId}`
          : `pb-books/${bookId}/learnerProgress/${userId}/questions/${update.questionId}`;
        
        const progressRef = doc(db, progressPath);
        
        batch.set(progressRef, {
          box: update.newBox,
          lastReviewed: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(update.isSubQuestion && { parentQuestionId: update.parentQuestionId })
        }, { merge: true });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error batch updating learner progress:', error);
      throw error;
    }
  },

  // Get box statistics for a learner
  async getLearnerBoxStats(bookId, userId) {
    try {
      const progress = await this.getLearnerProgress(bookId, userId);
      
      const stats = { 1: 0, 2: 0, 3: 0, 4: 0 };
      
      Object.values(progress.questions).forEach(p => {
        if (p.box >= 1 && p.box <= 4) {
          stats[p.box]++;
        }
      });

      Object.values(progress.subquestions).forEach(p => {
        if (p.box >= 1 && p.box <= 4) {
          stats[p.box]++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting learner box stats:', error);
      throw error;
    }
  },

  // Get tests for a book
  async getTests(bookId) {
    try {
      const testsRef = collection(db, 'pb-books', bookId, 'tests');
      const q = query(testsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching tests:', error);
      throw error;
    }
  },

  // Add a test
  async addTest(bookId, testData) {
    try {
      const testsRef = collection(db, 'pb-books', bookId, 'tests');
      const docRef = await addDoc(testsRef, {
        ...testData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.testCount || 0;

      await updateDoc(bookRef, {
        testCount: currentCount + 1,
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...testData };
    } catch (error) {
      console.error('Error adding test:', error);
      throw error;
    }
  },

  // Delete a test
  async deleteTest(bookId, testId) {
    try {
      const testRef = doc(db, 'pb-books', bookId, 'tests', testId);
      await deleteDoc(testRef);

      const bookRef = doc(db, 'pb-books', bookId);
      const bookDoc = await getDoc(bookRef);
      const currentCount = bookDoc.data()?.testCount || 0;

      await updateDoc(bookRef, {
        testCount: Math.max(0, currentCount - 1),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting test:', error);
      throw error;
    }
  },

  // Save test result
  async saveTestResult(bookId, resultData) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');
      const docRef = await addDoc(resultsRef, {
        ...resultData,
        createdAt: serverTimestamp()
      });

      return { id: docRef.id, ...resultData };
    } catch (error) {
      console.error('Error saving test result:', error);
      throw error;
    }
  },

  // Get all test results for a book
  async getTestResults(bookId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');
      const q = query(resultsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching test results:', error);
      throw error;
    }
  },

  // Get user's test results
  async getUserTestResults(bookId, userId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'testResults');
      const q = query(resultsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching test results:', error);
      throw error;
    }
  },

  async getQuestionCategories(bookId) {
    try {
      const questions = await this.getBookQuestions(bookId);
      
      const subjects = new Set();
      const grades = new Set();
      const topics = new Set();

      questions.forEach(q => {
        if (q.subject) subjects.add(q.subject);
        if (q.grade) grades.add(q.grade);
        if (q.topics && Array.isArray(q.topics)) {
          q.topics.forEach(topic => topics.add(topic));
        }

        if (q.type === 'stem' && q.subQuestions) {
          q.subQuestions.forEach(subQ => {
            if (subQ.topics && Array.isArray(subQ.topics)) {
              subQ.topics.forEach(topic => topics.add(topic));
            }
          });
        }
      });

      return {
        subjects: Array.from(subjects).sort(),
        grades: Array.from(grades).sort(),
        topics: Array.from(topics).sort()
      };
    } catch (error) {
      console.error('Error fetching question categories:', error);
      throw error;
    }
  },

  // Filter questions by categories
  filterQuestionsByCategories(questions, filters) {
    if (!filters.subject && !filters.grade && (!filters.topics || filters.topics.length === 0)) {
      return questions;
    }

    return questions.filter(q => {
      let matches = true;

      if (filters.subject && q.subject !== filters.subject) {
        matches = false;
      }

      if (filters.grade && q.grade !== filters.grade) {
        matches = false;
      }

      if (filters.topics && filters.topics.length > 0) {
        const questionTopics = q.topics || [];
        const hasMatchingTopic = filters.topics.some(topic => questionTopics.includes(topic));
        if (!hasMatchingTopic) {
          matches = false;
        }
      }

      return matches;
    });
  }
};